// Engine Runner (Standard-tier tab).
//
// A universal console: everyone with access sees it; the engine picker is
// filtered/locked by the caller's tier per engine-entitlements.json (the same
// manifest the server enforces). Pick an engine, edit its preset JSON (seeded
// from the group's defaults function), run it client-side on the wasm engine,
// and read the result JSON plus any GeoNum trust verdict. The client filter is
// UX; the server gate in ComputeController is authoritative for the API path.

import { getLicense, hasFullAccess } from './auth.js';
import { WASM_ENGINES } from './hpc-lab.js';
import { applySpec, sliceRequest } from './workbench-spec.js';

const ALL_ACCESS = ['admin', 'research', 'enterprise'];

// Correct per-engine request payloads, reused from the HPC Lab registry (the
// authoritative source). Keyed by the run_X function name. Only engines that
// have a preset here are offered, so every engine the picker shows runs with a
// valid request and returns real values - never an empty {results:[]}.
const PRESETS = {};
try {
  for (const k in WASM_ENGINES) {
    const e = WASM_ENGINES[k];
    const fn = e && e.fn && e.fn.name;
    if (fn && typeof e.defaults === 'function') {
      try { PRESETS[fn] = e.defaults(); } catch (_) { /* skip */ }
    }
  }
} catch (_) { /* registry unavailable - picker will show nothing runnable */ }

let manifest = null;
let worker = null;
let reqId = 0;
const pending = new Map();
let selected = null; // { fn, label, group }

// ---- generic worker bridge ------------------------------------------------

function getWorker() {
  if (!worker) {
    worker = new Worker(new URL('engine-runner.worker.js', import.meta.url.replace(new RegExp('chunks/[^/]+$'), '')), { type: 'module' });
    worker.onmessage = (e) => {
      const { id, ok, result, error } = e.data || {};
      const p = pending.get(id);
      if (!p) return;
      pending.delete(id);
      ok ? p.resolve(result) : p.reject(new Error(error));
    };
    worker.onerror = (e) => {
      for (const p of pending.values()) p.reject(new Error(e.message || 'worker error'));
      pending.clear();
    };
  }
  return worker;
}

function call(fn, json) {
  return new Promise((resolve, reject) => {
    const id = ++reqId;
    pending.set(id, { resolve, reject });
    getWorker().postMessage({ id, fn, json });
  });
}

// ---- entitlement (mirror of EngineEntitlementService / auth.js) ------------

function entitled(group, lic) {
  const tier = lic?.tier || 'trial';
  const mods = lic?.modules || [];
  if (ALL_ACCESS.includes(tier)) return true;
  if (group.module) {
    return mods.includes(group.module) || mods.includes('hpclab')
      || mods.includes(group.module + '_z4c') || mods.includes(group.module + '_addon');
  }
  // Active trial is full access incl. HPC Lab + DFT until it ends (addons stay gated).
  const isActiveTrial = tier === 'trial' && lic?.isActive;
  if (group.tier === 'enterprise') return mods.includes('hpclab') || isActiveTrial;
  return hasFullAccess(tier) || mods.includes(group.id) || isActiveTrial;
}

function gateLabel(group) {
  if (group.module) return group.module + ' addon';
  if (group.tier === 'enterprise') return 'Enterprise / HPC Lab';
  if (group.tier === 'pro') return 'Pro';
  return 'Standard';
}

// ---- rendering ------------------------------------------------------------

async function loadManifest() {
  if (manifest) return manifest;
  const res = await fetch('/engine-entitlements.json', { cache: 'no-cache' });
  manifest = await res.json();
  return manifest;
}

function setStatus(msg) {
  const el = document.getElementById('er-status');
  if (el) el.textContent = msg || '';
}

function pretty(s) {
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
}

async function build() {
  const root = document.getElementById('er-picker');
  if (!root) return;
  let m;
  try { m = await loadManifest(); }
  catch (e) { root.innerHTML = '<div class="er-empty">Could not load the engine catalog.</div>'; return; }
  const lic = getLicense();
  root.innerHTML = '';
  // Vertical rail of group toggles; each opens its engines as a hover flyout.
  const rail = document.createElement('div');
  rail.className = 'er-rail';
  for (const g of m.groups) {
    if (g.module) continue; // add-ons (bssn/ligo/grmhd) are their own thing - not in the workbench
    const ok = entitled(g, lic);
    const tierClass = g.module ? 'module' : (g.tier || 'standard');
    const list = document.createElement('div');
    list.className = 'er-engines';
    let shown = 0;
    for (const e of g.engines) {
      // Offer an engine if we can seed a valid request for it: either a
      // hand-written PRESET, or - preferred - the engine's OWN defaults via the
      // group/engine get_*_defaults export (params come from the engines).
      if (!PRESETS[e.fn] && !(e.defaultsFn || g.defaultsFn)) continue;
      shown++;
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'er-engine' + (ok ? '' : ' er-locked');
      item.innerHTML = ok ? e.label : `<i class="fas fa-lock"></i> ${e.label}`;
      item.title = ok ? e.fn : `Requires ${gateLabel(g)}`;
      if (ok) item.addEventListener('click', () => selectEngine(e, g, item));
      else item.addEventListener('click', () => setStatus(`${e.label} requires ${gateLabel(g)}.`));
      list.appendChild(item);
    }
    if (shown === 0) continue; // hide groups with no runnable engines
    const grp = document.createElement('div');
    grp.className = 'er-grp';
    const badge = g.module ? (g.module + ' addon') : g.tier;
    const toggle = document.createElement('div');
    toggle.className = 'er-grp-toggle';
    toggle.innerHTML = `<span class="er-grp-name">${g.label}</span>`
      + `<span class="er-tier-badge er-tier-${tierClass}">${badge}</span>`
      + `<i class="fas fa-chevron-right er-chev"></i>`;
    const flyout = document.createElement('div');
    flyout.className = 'er-flyout';
    flyout.appendChild(list);
    grp.appendChild(toggle);
    grp.appendChild(flyout);
    rail.appendChild(grp);
  }
  if (!rail.children.length) { root.innerHTML = '<div class="er-empty">No runnable engines for your plan yet.</div>'; return; }
  root.appendChild(rail);
}

async function selectEngine(e, g, item) {
  document.querySelectorAll('.er-engine').forEach(x => x.classList.remove('active'));
  item.classList.add('active');
  // Mark the owning group so the rail shows which group holds the active engine.
  document.querySelectorAll('.er-grp.er-has-active').forEach(x => x.classList.remove('er-has-active'));
  item.closest('.er-grp')?.classList.add('er-has-active');
  selected = { fn: e.fn, label: e.label, group: g, defaults: {} };
  document.getElementById('er-title').textContent = `${g.label} - ${e.label}`;
  document.getElementById('er-fn').textContent = e.fn;
  document.getElementById('er-result').textContent = '';
  document.getElementById('er-trust').innerHTML = '';
  renderChart(null);
  const editor = document.getElementById('er-json');
  editor.value = '';
  setStatus('loading parameters from the engine ...');

  // Source the request from the engine's OWN defaults when available: call the
  // group/engine get_*_defaults export (no args) through the worker, then slice
  // the (often grouped) bundle to this engine's request via the manifest
  // requestPath. Fall back: raw bundle if the slice is empty, then the
  // hand-written PRESET. The defaults double as the freeform-spec whitelist.
  let req = null, bundle = null;
  const dfn = e.defaultsFn || g.defaultsFn;
  if (dfn) {
    try { bundle = JSON.parse(await call(dfn, null)); }
    catch (_) { /* defaults export unavailable */ }
  }
  // Priority: a manifest requestPath means this engine is mapped, so use its
  // engine-sourced sliced request; else preserve a known-good PRESET (no
  // regression to engines that already worked); else fall back to the raw
  // bundle for a newly-exposed engine (best-effort, user can edit).
  // Explicit manifest requestPath wins; otherwise auto-derive a candidate key
  // from the fn (run_X_scan / run_X -> "X") and slice if the bundle has it.
  let rp = e.requestPath;
  if (rp == null && bundle != null && typeof bundle === 'object' && !Array.isArray(bundle)) {
    const cand = String(e.fn).replace(/^run_/, '').replace(/_scan$/, '');
    if (cand in bundle) rp = cand;
  }
  if (rp != null && bundle != null) {
    const sliced = sliceRequest(bundle, rp);
    if (sliced != null) req = sliced;
  }
  if (req == null && PRESETS[e.fn]) req = PRESETS[e.fn];
  if (req == null && bundle != null) req = bundle;

  // Ignore a stale select (user clicked another engine while this awaited).
  if (!selected || selected.fn !== e.fn) return;
  selected.defaults = (req && typeof req === 'object') ? req : {};
  editor.value = req != null ? JSON.stringify(req, null, 2) : '{}';
  setStatus('Edit the JSON, or add freeform overrides (one per line), e.g.  radial_points = 200');
  updateSandboxBar();
}

// ---- Sandbox bridge -------------------------------------------------------
//
// The Sandbox workspace (js/sandbox.js) saves/loads engine params and captures
// run history. The runner exposes its current selection + params via window so
// sandbox.js stays decoupled, and offers Save/Load affordances when entitled.

// Parse the current editor contents into a params object (JSON edit OR the
// freeform `path = value` spec merged onto the seeded defaults). Returns null
// when there is no valid params object.
function currentParams() {
  const editor = document.getElementById('er-json');
  if (!editor) return null;
  const txt = (editor.value || '').trim() || '{}';
  try { const o = JSON.parse(txt); return (o && typeof o === 'object' && !Array.isArray(o)) ? o : {}; }
  catch (_) {
    try { const { value, errors } = applySpec(selected?.defaults || {}, txt); return errors.length ? (selected?.defaults || {}) : value; }
    catch (_) { return selected?.defaults || {}; }
  }
}

// window.__sandboxCurrentRun() -> the active engine selection + params, or null.
function sandboxCurrentRun() {
  if (!selected) return null;
  const g = selected.group || {};
  return {
    kind: 'engine',
    ref: selected.fn,
    label: selected.label || selected.fn,
    group: (g.module || g.id || ''),
    params: currentParams() || {},
  };
}

// window.__sandboxLoadParams(set) -> select the matching engine (if found in the
// picker) and load its params into the editor. Returns true if the engine was
// selected, false if only the params were loaded (user must pick the engine).
function sandboxLoadParams(set) {
  if (!set || !set.params) return false;
  const editor = document.getElementById('er-json');
  const apply = () => { if (editor) { editor.value = JSON.stringify(set.params, null, 2); setStatus('Loaded params from Sandbox.'); } };
  // Try to click the matching engine button so defaults/title/fn are set first,
  // then overwrite the editor with the saved params.
  const ref = set.ref || '';
  const btn = Array.from(document.querySelectorAll('.er-engine'))
    .find(b => (b.title === ref) || (b.dataset && b.dataset.fn === ref));
  if (btn) {
    btn.click();
    // selectEngine is async (loads defaults); apply after it settles.
    setTimeout(apply, 250);
    return true;
  }
  apply();
  return false;
}

if (typeof window !== 'undefined') {
  window.__sandboxCurrentRun = sandboxCurrentRun;
  window.__sandboxLoadParams = sandboxLoadParams;
}

// Save-to-Sandbox affordance: a small button in the runbar that saves the
// current engine + params as a Sandbox parameter set. Shown only when entitled
// (window.__sandboxEntitled set by sandbox.js after auth).
function updateSandboxBar() {
  const bar = document.getElementById('er-runbar');
  if (!bar) return;
  const entitled = typeof window !== 'undefined' && typeof window.__sandboxEntitled === 'function' && window.__sandboxEntitled();
  let btn = document.getElementById('er-save-sandbox');
  if (!entitled || !selected) { if (btn) btn.style.display = 'none'; return; }
  if (!btn) {
    btn = document.createElement('button');
    btn.id = 'er-save-sandbox';
    btn.type = 'button';
    btn.className = 'btn btn-sm btn-ghost';
    btn.innerHTML = '<i class="fas fa-flask"></i> Save to Sandbox';
    btn.title = 'Save this engine and its parameters to your Sandbox';
    btn.addEventListener('click', saveToSandbox);
    bar.appendChild(btn);
  }
  btn.style.display = '';
}

async function saveToSandbox() {
  const cur = sandboxCurrentRun();
  if (!cur) return;
  const label = prompt('Save parameter set as', cur.label || cur.ref);
  if (label == null) return;
  try {
    const mod = await import('./sandbox.js');
    await mod.sbFetch('/api/sandbox/param-sets', {
      method: 'POST',
      body: mod.normalizeParamSet({ kind: cur.kind, ref: cur.ref, label, params: cur.params }),
    });
    window.gdbsToastSuccess?.('Saved to Sandbox.', { timeoutMs: 3500 });
  } catch (e) {
    window.gdbsToastError?.('Save to Sandbox failed: ' + (e.message || e), { timeoutMs: 5000 });
  }
}

// GeoNum trust/drift is reported in different places by different engines:
// top-level, under a `geonum` object, in a `summary`, or per-result inside
// `results[0]`. Search the common scopes and the known field names.
function pickField(o, keys) {
  if (!o || typeof o !== 'object') return null;
  for (const k of keys) if (o[k] != null) return o[k];
  return null;
}
// Same red->yellow->green scale the rest of the app uses for coherence tiers
// (tierColor in fluids-tabs.js): 0 = red, 0.5 = yellow, 1 = green.
function cohColor(val) {
  const v = parseFloat(val); let r, g, b;
  if (v < 0.5) { const t = v / 0.5; r = 220; g = Math.round(50 + 170 * t); b = 50; }
  else { const t = (v - 0.5) / 0.5; r = Math.round(220 - 180 * t); g = 200; b = Math.round(50 + 30 * t); }
  return `rgb(${r},${g},${b})`;
}
// Extract just the GeoNum trust verdict token from a result object (for the
// Sandbox run-history capture). Returns '' when no real verdict is present.
//
// BOTH WIRE SHAPES are accepted: the canonical dual object under geonum_trust
// (mantissa/magnitude/drift/cancel_drift) and the legacy bare verdict string.
// Deliberately delegated to geonum-trust.fromResult so this reader and the GTI
// badge can never disagree about what a result says.
//
// HONEST LIMIT: the Sandbox run-history row holds ONE string
// (Models/Sandbox/SandboxModels.cs:84 `public string Trust`), so only the
// MANTISSA half survives persistence. Mantissa is the conservative half (it is
// the one degraded by ordinary arithmetic), so the saved row under-claims rather
// than over-claims - but magnitude IS lost until that model can hold a pair.
//
// The six canonical verdict tokens, index 0..5, matching wasm/src/geonum.rs
// Trust::name() and js/gti-ladder.js GTI_NAMES. Anything else (a method name like
// "multigrid_v_cycle:4_levels", an ad-hoc 'UNTRACKED', a formatted status string)
// is NOT a verdict and must never render as one.
const _VERDICTS = ['EXACT', 'PRECISE', 'VALID', 'DEGRADED', 'UNRELIABLE', 'CANCELLED'];
function extractVerdict(obj) {
  if (!obj || typeof obj !== 'object') return '';
  try {
    const t = window.gdbsTrust && typeof window.gdbsTrust.fromResult === 'function'
      ? window.gdbsTrust.fromResult(obj) : null;
    if (t && t.mantissa) return t.mantissa;
    if (t && t.trust) return t.trust;
  } catch (_) { /* fall through to the local scan */ }
  // Local fallback for the case where geonum-trust.js has not registered yet
  // (module load order). Same field list, string shape only - it never invents a
  // magnitude verdict from a missing field.
  const r0 = Array.isArray(obj.results) ? obj.results[0] : null;
  const scopes = [obj, obj.geonum, obj.summary, r0, r0 && r0.geonum].filter(Boolean);
  for (const s of scopes) {
    for (const k of ['geonum_trust', 'energy_trust', 'trust_verdict', 'trust_tier', 'trust', 'geonum_trust_tier']) {
      const v = s && s[k];
      if (typeof v === 'string' && _VERDICTS.includes(v.trim().toUpperCase())) return v.trim().toUpperCase();
      // The canonical object shape: read its mantissa half.
      if (v && typeof v === 'object' && typeof v.mantissa === 'string'
        && _VERDICTS.includes(v.mantissa.trim().toUpperCase())) return v.mantissa.trim().toUpperCase();
    }
    // The split shape emitted by wasm/src/coexistence.rs (mantissa_trust /
    // availability_mantissa_trust).
    for (const k of ['mantissa_trust', 'availability_mantissa_trust']) {
      const v = s && s[k];
      if (typeof v === 'string' && _VERDICTS.includes(v.trim().toUpperCase())) return v.trim().toUpperCase();
    }
  }
  return '';
}

function renderTrust(obj) {
  const el = document.getElementById('er-trust');
  if (!el) return;
  if (!obj || typeof obj !== 'object') { el.innerHTML = ''; return; }
  const r0 = Array.isArray(obj.results) ? obj.results[0] : null;
  const scopes = [obj, obj.geonum, obj.summary, r0, r0 && r0.geonum].filter(Boolean);
  // NOTE: this function used to re-scan the result for a SINGLE verdict token and a
  // SINGLE drift figure, then throw both away - the badge below has rendered the
  // verdict since the unified GTI marker landed. That dead scan was the second
  // shared single-verdict collapse in the app (logging.txt SECTION 8, D08), so it
  // is gone rather than merely corrected: the ONE reader is
  // geonum-trust.fromResult, which accepts the canonical dual object, the split
  // mantissa/magnitude fields and the legacy bare string, and renders two chips.
  // Only the tiered-coherence field is still resolved here, because that is NOT a
  // trust verdict and the badge does not draw it.
  let coh = null;
  for (const s of scopes) {
    if (coh == null) coh = pickField(s, ['tiered_coherence', 'coherence']);
  }
  let html = '';
  // Unified GTI marker (verdict + drift detail + dormant predictive arrow + click
  // audit), shared with every other surface - replaces the old bespoke Trust:/Drift:
  // chips so the whole platform reads behind one GTI badge. It handles the untracked
  // case internally (honest "Untracked"), so no separate fallback is needed.
  if (window.gdbsTrust && typeof window.gdbsTrust.html === 'function') html += window.gdbsTrust.html(obj);
  // Every substrate engine reports a tiered-coherence (bloom-gradient) field:
  // core (7D), magnitude (9D), phase (11D), proportion (13D), plus relational
  // layers. Render it as the app-canonical gradient bar (one segment per tier),
  // NOT a single-number verdict - the tiers live on different structural scales
  // (core is a 2-product/3-product ratio), so any min/mean collapse misleads.
  // This is geometric coherence, NOT the GeoNum drift-trust verdict.
  if (coh && typeof coh === 'object') {
    const f = v => (typeof v === 'number' ? v.toFixed(3) : '-');
    const tiers = [['Core', coh.core], ['Mag', coh.magnitude], ['Phase', coh.phase], ['Prop', coh.proportion]];
    if (Array.isArray(coh.relational)) {
      coh.relational.forEach((r, i) => {
        const l = i + 2;
        if (Array.isArray(r)) tiers.push([`Mag×${l}`, r[0]], [`Phase×${l}`, r[1]], [`Prop×${l}`, r[2]]);
      });
    }
    const all = tiers.filter(t => typeof t[1] === 'number');
    if (all.length) {
      const w = (100 / all.length).toFixed(2);
      const segs = all.map(([name, val]) =>
        `<div class="tc-seg" style="width:${w}%;background:${cohColor(Math.max(0.08, val))}"><span class="tc-tip">${name}<br>${f(val)}</span></div>`
      ).join('');
      const labels = all.map(([name, val]) =>
        `<span class="er-cohlabel"><i style="background:${cohColor(Math.max(0.08, val))}"></i>${name} <b>${f(val)}</b></span>`
      ).join('');
      html += `<div class="er-cohbar"><span class="er-cohbar-label">GeoNum coherence (bloom gradient)</span>`
        + `<div class="tc-wrap"><div class="tc-bar">${segs}</div></div>`
        + `<div class="er-cohlabels">${labels}</div></div>`;
    }
  }
  el.innerHTML = html || '<span class="er-trust-none">No GeoNum trust field reported by this engine.</span>';
}

// Generic result chart: auto-detect numeric series in the result and plot them
// with the global Chart.js - no per-engine config. Either results[] rows with
// numeric fields (one line per field) or top-level numeric arrays.
let erChart = null;
const CHART_PAL = ['#7c3aed', '#2563eb', '#059669', '#dc2626', '#d97706', '#0891b2'];
function findSeries(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const rows = Array.isArray(obj.results) ? obj.results : (Array.isArray(obj) ? obj : null);
  if (rows && rows.length && rows[0] && typeof rows[0] === 'object') {
    const keys = Object.keys(rows[0]).filter(k => typeof rows[0][k] === 'number');
    if (keys.length) {
      return {
        labels: rows.map((_, i) => i + 1),
        datasets: keys.slice(0, 6).map((k, i) => ({
          label: k, data: rows.map(r => (typeof r[k] === 'number' ? r[k] : null)),
          borderColor: CHART_PAL[i % CHART_PAL.length], backgroundColor: 'transparent', pointRadius: 0, borderWidth: 1.5,
        })),
      };
    }
  }
  const arrs = Object.entries(obj).filter(([, v]) => Array.isArray(v) && v.length && v.every(x => typeof x === 'number'));
  if (arrs.length) {
    const n = Math.max(...arrs.map(([, v]) => v.length));
    return {
      labels: Array.from({ length: n }, (_, i) => i + 1),
      datasets: arrs.slice(0, 6).map(([k, v], i) => ({
        label: k, data: v, borderColor: CHART_PAL[i % CHART_PAL.length], backgroundColor: 'transparent', pointRadius: 0, borderWidth: 1.5,
      })),
    };
  }
  return null;
}
function renderChart(obj) {
  const wrap = document.getElementById('er-chart-wrap');
  const cv = document.getElementById('er-chart');
  if (erChart) { try { erChart.destroy(); } catch (_) {} erChart = null; }
  if (!cv || !window.Chart) { if (wrap) wrap.style.display = 'none'; return; }
  const s = findSeries(obj);
  if (!s) { if (wrap) wrap.style.display = 'none'; return; }
  if (wrap) wrap.style.display = '';
  erChart = new window.Chart(cv.getContext('2d'), {
    type: 'line', data: s,
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { labels: { boxWidth: 10, font: { size: 10 } } } },
      scales: { x: { ticks: { maxTicksLimit: 12, font: { size: 9 } } }, y: { ticks: { font: { size: 9 } } } },
    },
  });
}

async function run() {
  if (!selected) { setStatus('Pick an engine first.'); return; }
  const editor = document.getElementById('er-json');
  const txt = editor.value.trim() || '{}';
  // Two input modes, deterministic: valid JSON is used as-is (direct edit of the
  // seeded request); otherwise the text is a freeform `path = value` spec parsed
  // and merged onto the engine's seeded defaults (the whitelist) - NO AI.
  let json;
  try {
    JSON.parse(txt);
    json = txt;
  } catch (_) {
    const { value, errors } = applySpec(selected.defaults || {}, txt);
    if (errors.length) { setStatus('Spec errors - ' + errors.join('; ')); return; }
    json = JSON.stringify(value);
  }
  setStatus(`running ${selected.fn} ...`);
  document.getElementById('er-result').textContent = '';
  document.getElementById('er-trust').innerHTML = '';
  const _t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  try {
    // Log every interactive run on the MAIN thread (the engines.js _w shim
    // no-ops inside the worker - no `window` there), wrapping the worker call
    // exactly like plugin-host.engine(): guard on window.gdbslog, pass a thunk,
    // await the promise time() returns. Module = engine group, action = the run
    // name minus the run_ prefix (matching engines.js _w labeling).
    const _mod = (selected.group && (selected.group.module || selected.group.id)) || 'engine-runner';
    const _act = String(selected.fn).replace(/^run_/, '');
    const _glog = (typeof window !== 'undefined' && window.gdbslog) ? window.gdbslog.getLogger(_mod) : null;
    const _call = () => call(selected.fn, json);
    // Mark this op so the Sandbox gdbslog bridge skips the op record (which fires
    // first) and lets our params-rich direct capture below be the single row.
    try { window.__sandboxMarkDirectOp && window.__sandboxMarkDirectOp(_act); } catch (_) {}
    const out = _glog ? await _glog.time(_act, _call) : await _call();
    let obj = null;
    try { obj = JSON.parse(out); } catch (_) { /* non-JSON result */ }
    document.getElementById('er-result').textContent = obj ? JSON.stringify(obj, null, 2) : String(out);
    renderTrust(obj);
    renderChart(obj);
    // Best-effort: capture this run into the Sandbox run history (no-op when not
    // entitled; never blocks or breaks the run).
    try {
      if (typeof window !== 'undefined' && typeof window.__sandboxCaptureRun === 'function') {
        const _ms = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - _t0);
        window.__sandboxCaptureRun({
          kind: 'engine', ref: selected.fn, label: selected.label || selected.fn,
          params: currentParams() || {}, trust: extractVerdict(obj), ms: _ms,
        });
      }
    } catch (_) {}
    // Worker-run engines do not pass through engines.js _w (no `window` in the
    // worker), so stamp the shared last-run here, scoped to the active view, for
    // the universal report + repro bundle.
    try {
      if (typeof window !== 'undefined') {
        const vid = window.gdbsReport && window.gdbsReport.findActiveArea ? (window.gdbsReport.findActiveArea().view?.id || 'view-engine-runner') : 'view-engine-runner';
        window.__gdbsLastRun = { domain: _mod, label: _act, req: json, result: out, viewId: vid, ts: Date.now() };
      }
    } catch (_) {}
    setStatus('done');
  } catch (err) {
    document.getElementById('er-result').textContent = String(err.message || err);
    setStatus('error');
  }
}

// ---- wiring ---------------------------------------------------------------

function hook() {
  // Rebuild the picker each time the view opens so the license (loaded after
  // auth) and any tier change are reflected.
  document.querySelectorAll('.nav-item[data-view="engine-runner"]').forEach(btn => {
    btn.addEventListener('click', () => build());
  });
  const runBtn = document.getElementById('er-run');
  if (runBtn) runBtn.addEventListener('click', run);
  // If the view is already active at load (deep link), build now.
  if (document.getElementById('view-engine-runner')?.classList.contains('active')) build();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hook);
else hook();
